import type { VoteEntity } from "@/appInterface/windows/VoteWindow/VoteResults/types";
import {
    getSanitizedComment,
    getSanitizedEventInfo,
    getSanitizedGroup,
    getSanitizedMessage,
    walkDeepSanitize,
} from "@/canv/utils";
import type {
    CreateOrDeleteParticipantProperty,
    CreateOrDeleteVoteProperty,
    EventStructure,
    PropertyBuildEventStructure,
    PropertyEventStructure,
} from "@/eventManager/events/types";
import type { EntityBaseState } from "@/shared/types/entityState";

export type SanitizedEvent = Omit<EventStructure, "TouchEventStructure"> & {
    vote?: EntityBaseState;
    comment?: EntityBaseState;
    group?: EntityBaseState;
    state?: EntityBaseState;
    property?: EntityBaseState;
};

export const addVote = (
    childUID: string,
    property: CreateOrDeleteVoteProperty,
    activeVote: VoteEntity,
) => {
    Object.values(activeVote.children).forEach((child) => {
        if (child.widgetData?.UID === childUID) {
            child.votes.push({
                token: property.token,
                accountName: property.accountName || "",
            });
        }
    });
};
export const removeVote = (childUID: string, token: string, activeVote: VoteEntity) => {
    for (const child of Object.values(activeVote.children)) {
        if (child.widgetData?.UID === childUID) {
            const idx = child.votes.findIndex((v) => v.token === token);
            if (idx >= 0) {
                child.votes.splice(idx, 1);
                break;
            }
        }
    }
};

export const addVoter = (property: CreateOrDeleteParticipantProperty, activeVote: VoteEntity) => {
    const hasVoter = (activeVote.participants || []).some(
        (voter) => voter.token === property.token,
    );
    if (hasVoter) return;
    activeVote.participants.push(property);
};

export const handlePropertyUpdate = (msg: PropertyEventStructure, activeVote: VoteEntity) => {
    if (msg.state.additionalDuration) {
        activeVote.duration += msg.state.additionalDuration;
    }
    if (msg.state.isResolved) {
        activeVote.isResolved = true;
    }
};

export const handlePropertyBuild = (msg: PropertyBuildEventStructure, activeVote: VoteEntity) => {
    if ("childUID" in msg.property && msg.fixType === "votes") {
        if (msg.action === "add") {
            addVote(msg.property.childUID, msg.property, activeVote);
        } else if (msg.action === "remove") {
            removeVote(msg.property.childUID, msg.property.token, activeVote);
        }
    }
    if (msg.fixType === "participants") {
        addVoter(msg.property as CreateOrDeleteParticipantProperty, activeVote);
    }
};

export function returnSanitizedCommentActionEvents(eventsData: EventStructure[]) {
    const commentsActions: SanitizedEvent[] = [];

    eventsData.forEach((event) => {
        if (event.type === "build") {
            const comment = event?.entities?.find((entity) => entity.entityType === "comment");
            const group = event?.entities?.find((entity) => entity.entityType === "group");
            if (comment && group) {
                const sanitizedEvent = getSanitizedEventInfo(event);
                sanitizedEvent.comment = getSanitizedComment(comment);
                sanitizedEvent.group = getSanitizedGroup(group);

                commentsActions.push(sanitizedEvent);
            }
        }
        if (event.type === "property" && event.state.entityType === "comment") {
            const sanitizedEvent = getSanitizedEventInfo(event);
            sanitizedEvent.state = getSanitizedComment(event.state);

            commentsActions.push(sanitizedEvent);
        }
        if (event.type === "propertyBuild" && event.fixType === "messages") {
            const sanitizedEvent = getSanitizedEventInfo(event);
            sanitizedEvent.property = getSanitizedMessage(event.property);

            commentsActions.push(sanitizedEvent);
        }
    });

    return commentsActions;
}

export function returnSanitizedVoteActionEvents(eventsData: EventStructure[]) {
    const voteActions: SanitizedEvent[] = [];

    eventsData.forEach((event) => {
        if (event.type === "build") {
            const vote = event?.entities?.find((entity) => entity.entityType === "vote");

            if (vote) {
                const sanitizedEvent = getSanitizedEventInfo(event);

                sanitizedEvent.vote = walkDeepSanitize(vote);

                voteActions.push(sanitizedEvent);
            }
        }
    });
    return voteActions;
}
